<template>
    <header v-if="showNewVersion" class="overview-header" data-bot="overview__header">
        <div class="overview-header__backlink">
            <CompassBackForwardButton
                :label="$t.__('common.actions.back')"
                :link="'javascript:history.back();'"
                type="secondary"
                size="small"
                back
            />
            <OverviewNav
                :overviewhash="overviewhash"
                :identity="identity"
                :splitter="splitter"
                :useSPARouting="true"
                class="flex-right"
            />
        </div>
        <div class="overview-header__title-and-info">
            <div class="overview-header__info">
                <EditText
                    class="overview-header__title"
                    :text="opportunity.name"
                    :canEdit="permissions.can_edit"
                    :loading="isLoadingTitle"
                    contentTypographyClass="typographies-heading-h1"
                    @update="handleTitleUpdate"
                >
                    <h2 class="overview-header__title-content typographies-heading-h1">
                        <span data-bot="overview__header--name">
                            {{ opportunity.name }}
                        </span>
                    </h2>
                </EditText>
                <div class="overview-header__company-and-source">
                    <div class="overview-header__company">
                        <CompassObject size="extra-small" />
                        <CompassLink :label="company.name" :link="company.link" type="secondary" />
                    </div>
                    <div
                        class="overview-header__opportunity-id typographies-paragraph-medium-regular"
                    >
                        {{ opportunity.idName }}
                    </div>
                    <div class="overview-header__source typographies-paragraph-medium-regular">
                        <EditSelect
                            :items="formattedSources"
                            :canEdit="permissions.can_edit"
                            size="small"
                            :loading="isLoadingSource"
                            @update="handleSourceUpdate"
                        />
                    </div>
                    <div class="overview-header__pipeline typographies-paragraph-medium-semibold">
                        <CompassTooltip
                            :content="opportunity.funnelName"
                            :arrow="false"
                            placement="bottom-start"
                        >
                            {{ pipeName }}
                        </CompassTooltip>
                    </div>
                </div>
            </div>

            <div class="overview-header__data">
                <div class="overview-header__status">
                    <CompassFormItem :label="$t.__('common.labels.status')" size="small">
                        <EditSelect
                            :items="statusesWithSelect"
                            :canEdit="permissions.can_edit"
                            :loading="isLoadingStatus"
                            :showTag="true"
                            @update="handleStatusUpdate"
                        />
                    </CompassFormItem>
                </div>
                <div v-if="hasEndDate && hasEndDateValid" class="overview-header__end">
                    <CompassFormItem
                        :label="$t.__('common.labels.expected_close_date')"
                        size="small"
                    >
                        <div class="overview-header__end-date">
                            <EditSimpleDate
                                :date="endDate"
                                :loading="isLoadingDate"
                                :canEdit="canEditDate"
                                @update="handleEndDateUpdate"
                            />
                        </div>
                    </CompassFormItem>
                </div>
                <div class="overview-header__potential">
                    <CompassFormItem :label="$t.__('common.labels.potential_amount')" size="small">
                        <EditText
                            :text="opportunity.potential"
                            :canEdit="permissions.can_edit"
                            :loading="isLoadingAmount"
                            :numberFormat="numformat"
                            contentTypographyClass="typographies-heading-h2"
                            @update="handlePotentialAmountUpdate"
                        >
                            <div class="overview-header__potential-amount typographies-heading-h2">
                                {{ $filters.numberFormatCurrency(opportunity.potential) }}
                            </div>
                        </EditText>
                    </CompassFormItem>
                </div>
                <CompassFormItem :label="$t.__('common.labels.one_or_more_staff')" size="small">
                    <EditMultipleUsers
                        :data="assignedStaffs"
                        :loading="isLoadingStaff"
                        @update="handleStaffUpdate"
                    />
                </CompassFormItem>
            </div>
        </div>

        <div class="overview-header__pipeline-and-actions">
            <PipelineStepper
                :pipeName="opportunity.funnelName"
                :steps="stepWithDate"
                :selectedStep="displayedStep"
                :canEdit="permissions.can_edit"
                @update="(step) => pickStep(step, true)"
            />

            <div class="overview-header__actions">
                <CompassMenu placement="bottom-end" customClass="overview-header__actions-menu">
                    <CompassIconButton icon="action" data-bot="overview-header__actions-button" />
                    <template #dropdown>
                        <CompassMenuItem
                            v-for="(action, index) in actionsWithAccess"
                            :key="index"
                            :disabled="!hasAccess(action.permission, permissions)"
                            :label="action.label"
                            @click="handleActionItemClick(action)"
                        />
                    </template>
                </CompassMenu>
                <CompassTooltip
                    :content="$t.__('common.labels.no_necessary_rights')"
                    :disabled="hasAccess('can_create_invoicing_docs', permissions)"
                >
                    <CompassButton
                        type="secondary"
                        :label="$t.__('common.actions.create.document')"
                        :disabled="!hasAccess('can_create_invoicing_docs', permissions)"
                        size="small"
                        @click="createDocument"
                    />
                </CompassTooltip>
                <CompassMenu placement="bottom-end" customClass="overview-header__schedule-menu">
                    <CompassButton
                        :label="$t.__('common.actions.schedule')"
                        type="secondary"
                        size="small"
                        dropdown
                    />
                    <template #dropdown>
                        <CompassMenuItem
                            v-for="(action, index) in allowedScheduledActions"
                            :key="index"
                            @click="handleClick(action.type)"
                        >
                            <CompassObject :object="action.icon" size="extra-small" />
                            <span>{{ action.label }}</span>
                        </CompassMenuItem>
                    </template>
                </CompassMenu>
            </div>
        </div>
    </header>
    <header v-else class="white-bkg bb-1 px-5 mt--5 ml--5 fs-3">
        <div class="flex-level flex-column ov-reset jc-space m-auto">
            <!-- MAIN INFO -->
            <div class="flex-row px-4 pt-3 pb-2 pt-0">
                <div class="w-3 no-shrink">
                    <!-- Avatar -->
                    <div class="relative">
                        <person-list
                            :list="opportunity.collab.list"
                            :placeholder="opportunity.collab.placeholder"
                        />
                    </div>

                    <!-- Status -->
                    <div class="relative br my-2">
                        <EditSelect
                            class="status-select"
                            :items="statusesWithSelect"
                            :canEdit="permissions.can_edit"
                            :loading="isLoadingStatus"
                            :showTag="true"
                            @update="handleStatusUpdate"
                        />
                    </div>
                    <!-- Id -->
                    <p class="m-0 gray-60">{{ opportunity.idName }}</p>
                </div>

                <!-- NAME AND STEP -->
                <div class="flex-column flex acenter step-container ovx-hidden h-auto">
                    <h2 class="mt-0 mb-1">{{ opportunity.name }}</h2>
                    <p class="m-0 gray-60">{{ opportunity.funnelName }}</p>

                    <el-select
                        v-if="hoverStep"
                        v-model="hoverStep"
                        class="step-select wp-10 br b-1-bordercolor-hover mx-auto"
                    >
                        <el-option
                            v-for="(step, index) in stepWithDate"
                            :key="index"
                            :label="step.name"
                            :value="step.id"
                        >
                        </el-option>
                    </el-select>
                    <el-select
                        v-else
                        v-model="selectedStep"
                        class="step-select wp-10 br b-1-bordercolor-hover mx-auto"
                        @change="setStep"
                        @visibleChange="handleStepSelectVisibleChange"
                    >
                        <el-option
                            v-for="(step, index) in stepWithDate"
                            :key="index"
                            :label="step.name"
                            :value="step.id"
                        >
                            <div class="flex-level">
                                <span v-if="isBeforeSelectedStep(index)" class="gray-60">
                                    {{ step.name }}
                                </span>
                                <span v-else-if="selectedStep === step.id" class="primary-50">
                                    {{ step.name }}
                                </span>
                                <span v-else class="gray-100">{{ step.name }}</span>

                                <span class="flex-right gray-60 ml-2">{{ step.probability }}%</span>
                            </div>
                        </el-option>
                    </el-select>
                    <el-steps :active="displayedStep" alignCenter class="opp-step ov-hidden">
                        <el-step
                            v-for="(step, index) in stepWithDate"
                            :key="index"
                            :class="{ cpointer: permissions.can_edit }"
                            @mouseover="stepHovered(step.id)"
                            @mouseleave="stepHovered(null)"
                            @click="pickStep(step)"
                        >
                            <template #icon>
                                <span class="el-step__icon-inner"> </span>
                            </template>
                        </el-step>
                    </el-steps>
                </div>

                <!-- POTENCIAL , DATE AND SOURCE -->
                <div class="w-3 no-shrink aright">
                    <h2 class="mt-0 mb-2">
                        {{ $filters.numberFormatCurrency(opportunity.potential) }}
                    </h2>
                    <div
                        v-if="hasEndDate && hasEndDateValid"
                        class="dinlineb br mb-2 opp-date"
                        :class="statusDateClass"
                        @click.prevent="openOppDatePicker"
                    >
                        <div v-show="activeDatePicker">
                            <el-date-picker
                                ref="datepicker"
                                v-model="endDate"
                                valueFormat="YYYY-MM-DD"
                                type="date"
                                class="opp-date--picker"
                                :format="dateFormatDisplay"
                                @visible-change="handleVisibleChange"
                            />
                        </div>
                        <span v-show="!activeDatePicker">
                            <i v-if="!activeDatePicker" class="mr-2" :class="statusDateIcon"> </i>
                            {{ opportunity.endDate }}
                        </span>
                    </div>
                    <p class="m-0 gray-60">{{ opportunity.source }}</p>
                </div>
            </div>

            <div class="flex-row bt-1">
                <div
                    class="flex-row flex-level br-1 px-4 my-2"
                    :class="isCorporation ? 'flex-item-50' : 'wp-10'"
                >
                    <!-- THIRD INFO -->
                    <div>
                        <el-tooltip
                            :content="$t.__(`overview.panels.OverviewPanel.deleted_company`)"
                            placement="top"
                            :disabled="isThirdActive"
                        >
                            <a
                                :href="isThirdActive ? company.link : null"
                                class="black-link semibold mb-1 block"
                                >{{ company.name }}</a
                            >
                        </el-tooltip>
                        <p class="grey my-0">
                            {{ isCorporation ? $l10n.__('Société') : $l10n.__('Particulier') }}
                        </p>
                    </div>
                    <div v-if="isThirdActive" class="flex-right aright">
                        <span class="copy-element block">
                            <a
                                v-if="companyPhone"
                                :href="
                                    phonecallProtocol + ':' + companyPhone.replace(/[^\+\d]/g, '')
                                "
                                class="dinlineb mb-1 black-link"
                                >{{ companyPhone }}</a
                            >
                            <SaasClipboard v-if="companyPhone" :data="companyPhone" />
                        </span>
                        <span class="copy-element block">
                            <email-link
                                v-if="company.email"
                                linkedtype="opportunity"
                                :linkedid="parseInt(informations.identity.id)"
                                :email="company.email"
                                :disabled="company.isArchived"
                                class="dinlineb"
                            >
                                {{ company.email }}
                            </email-link>
                            <SaasClipboard v-if="company.email" :data="company.email" />
                        </span>
                    </div>
                </div>

                <div v-if="isCorporation" class="flex-row flex-level flex-item-50 pl-4 my-2">
                    <!-- CONTACT INFO -->
                    <div>
                        <a class="black-link semibold mb-1 block" :href="mainContact.link">{{
                            mainContact.fullName
                        }}</a>
                        <p class="grey my-0">
                            <span v-if="mainContact.position">
                                {{ mainContact.position }}
                            </span>
                            <span v-else>
                                {{ $l10n.__('Contact') }}
                            </span>
                        </p>
                    </div>
                    <div class="flex-right aright pr-4 br-1">
                        <span v-if="mainContactPhone != ''" class="copy-element block">
                            <a
                                :href="
                                    phonecallProtocol +
                                    ':' +
                                    mainContactPhone.replace(/[^\+\d]/g, '')
                                "
                                class="dinlineb mb-1 black-link"
                                >{{ mainContactPhone }}</a
                            >
                            <SaasClipboard v-if="mainContactPhone" :data="mainContactPhone" />
                        </span>
                        <span v-if="mainContact.email" class="copy-element block">
                            <email-link
                                linkedtype="opportunity"
                                :linkedid="parseInt(informations.identity.id)"
                                class="dinlineb"
                            >
                                {{ mainContact.email }}
                            </email-link>
                            <SaasClipboard v-if="mainContact.email" :data="mainContact.email" />
                        </span>
                    </div>
                </div>
                <div class="pl-4 self-center">
                    <el-dropdown>
                        <CompassButton
                            :label="$t.__('common.labels.actions')"
                            type="secondary"
                            dropdown
                        />
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item
                                    v-for="(action, index) in actionsWithAccess"
                                    :key="index"
                                    :disabled="!hasAccess(action.permission, permissions)"
                                >
                                    <a
                                        v-if="action.type == 'function'"
                                        :class="
                                            !hasAccess(action.permission, permissions)
                                                ? 'link-disabled'
                                                : ''
                                        "
                                        @click="handleFunctionCall(action.value)"
                                    >
                                        {{ action.label }}
                                    </a>
                                    <a
                                        v-else-if="action.type == 'vuejs'"
                                        :onclick="buildUrl(action.value, parameters)"
                                        :class="
                                            !hasAccess(action.permission, permissions)
                                                ? 'link-disabled'
                                                : ''
                                        "
                                    >
                                        {{ action.label }}
                                    </a>
                                    <a
                                        v-else
                                        :href="buildUrl(action.value, parameters)"
                                        :class="
                                            !hasAccess(action.permission, permissions)
                                                ? 'link-disabled'
                                                : ''
                                        "
                                    >
                                        {{ action.label }}
                                    </a>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </div>
            </div>
        </div>
    </header>
</template>

<script>
import EmailLink from 'vcomponents/email/EmailLink.vue';
import Errors from 'Core/commons/Errors';
import PersonList from 'vcomponents/utilities/PersonList.vue';
import SaasClipboard from 'vcomponents/utilities/SaasClipboard.vue';
import { formatUrl, buildUrl, hasAccess } from '../../helpers';
import { createNamespacedHelpers } from 'vuex';
const { mapState } = createNamespacedHelpers('corp');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
import Feature from 'Services/Feature';
import EditSimpleDate from 'vcomponents/common/editInPlace/SimpleDate/SimpleDate.vue';
import EditSelect from 'vcomponents/common/editInPlace/Select/Select.vue';
import EditMultipleUsers from 'vcomponents/common/editInPlace/Avatar/Avatar.vue';
import EditText from 'vcomponents/common/editInPlace/Text/Text.vue';
import OpportunitiesService from 'api/opportunities/opportunitiesService';
import EmailMixins from 'mixins/EmailMixins';
import PipelineStepper from 'vcomponents/overview/PipelineStepper/PipelineStepper.vue';
const staffGetters = createNamespacedHelpers('staffs').mapGetters;
import OverviewNav from 'vcomponents/overview/OverviewNav.vue';
import { allStatus } from 'Dictionaries/statusColors';

export default {
    components: {
        'person-list': PersonList,
        'email-link': EmailLink,
        SaasClipboard,
        EditSimpleDate,
        EditSelect,
        PipelineStepper,
        EditText,
        EditMultipleUsers,
        OverviewNav,
    },
    mixins: [EmailMixins],
    props: {
        overviewhash: {
            type: String,
            required: true,
            default: '',
        },
        actions: {
            type: Array,
            required: false,
            default: () => [],
        },
    },

    data() {
        return {
            hoverStep: null,
            activeDatePicker: false,
            localDate: null,
            errors: new Errors(),
            isLoadingDate: false,
            isLoadingStatus: false,
            isLoadingTitle: false,
            isLoadingAmount: false,
            isLoadingStaff: false,
            isLoadingSource: false,
            scheduledActions: [
                {
                    icon: 'sent-email',
                    type: 'email',
                    label: this.$t.__('overview.header.scheduleActions.email'),
                    permission: true,
                },
                {
                    icon: 'task',
                    type: 'task',
                    label: this.$t.__('overview.header.scheduleActions.task'),
                    permission: true,
                },
                {
                    icon: 'calendar',
                    type: 'event',
                    label: this.$t.__('overview.header.scheduleActions.event'),
                    permission: true,
                },
                {
                    icon: 'phone',
                    type: 'phonecallScheduled',
                    label: this.$t.__('overview.header.scheduleActions.call'),
                    permission: this.$store.state.corp.plugins.includes('phonecall'),
                },
            ],
            createActions: [],
            splitter: /\/(\d+)/,
            blockHoverStep: false,
        };
    },
    computed: {
        ...mapState(['phonecallProtocol', 'timeformat', 'uiLang', 'numformat']),
        ...staffGetters({
            staff: 'getStaffs',
        }),
        showNewVersion() {
            return Feature.isEnabled('overview-info-sidebar');
        },
        dateFormatDisplay() {
            return this.timeformat === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        },
        endDate: {
            get() {
                return moment(
                    this.informations.informations.find((index) => index.key === 'endDate').value,
                ).format('YYYY-MM-DD');
            },
            set(value) {
                this.updateDueDate(value);
            },
        },
        hasEndDateValid() {
            const endDate = moment(
                this.informations.informations.find((index) => index.key === 'endDate').value,
            ).format('YYYY-MM-DD');
            return endDate !== 'Invalid date';
        },
        actionsWithAccess() {
            return this.actions.filter((action) => hasAccess(action.display, this.permissions));
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        hasEndDate() {
            const value = this.informations.informations.find(
                (index) => index.key == 'endDate',
            ).value;
            return value !== '0000-00-00 00:00:00' && value !== '';
        },
        parameters() {
            return [
                {
                    key: 'id',
                    value: this.linkedid,
                },
                {
                    key: 'type',
                    value: this.linkedtype,
                },
                {
                    key: 'can_delete',
                    value: this.$store.getters.permissions['can_delete'],
                },
            ];
        },
        informations() {
            return this.$store.getters.dataFetch;
        },
        identity() {
            return this.$store.getters.identity;
        },
        mainContact() {
            if (this.informations.contacts.length > 0) {
                const mainContact = {};
                mainContact.id = this.informations.contacts[0].find(
                    (index) => index.key == 'id',
                ).value;
                mainContact.fullName = this.informations.contacts[0].find(
                    (index) => index.key == 'fullName',
                ).value;
                mainContact.email = this.informations.contacts[0].find(
                    (index) => index.key == 'email',
                ).value;
                mainContact.position = this.informations.contacts[0].find(
                    (index) => index.key == 'position',
                ).value;
                mainContact.phone = this.informations.contacts[0].find(
                    (index) => index.key == 'phone',
                ).value;
                mainContact.mobile = this.informations.contacts[0].find(
                    (index) => index.key == 'mobile',
                ).value;
                mainContact.link = this.informations.contacts[0].find(
                    (index) => index.key == 'link',
                ).value;
                mainContact.peopleId = this.informations.contacts[0].find(
                    (index) => index.key == 'peopleId',
                ).value;
                return mainContact;
            } else return false;
        },
        mainContactPhone() {
            if (this.mainContact) {
                return this.mainContact.mobile !== ''
                    ? this.mainContact.mobile
                    : this.mainContact.phone !== ''
                        ? this.mainContact.phone
                        : '';
            }
            return '';
        },
        company() {
            const company = {};
            company.id = this.informations.third.find((index) => index.key == 'id').value;
            company.active = this.informations.third.find((index) => index.key == 'active').value;
            company.name = this.informations.third.find((index) => index.key == 'name').value;
            company.link = this.informations.third.find((index) => index.key == 'link').value;
            company.type = this.informations.third.find((index) => index.key == 'type').value;
            company.relationType = this.informations.third.find(
                (index) => index.key == 'relationType',
            ).value;
            company.web = this.informations.third.find((index) => index.key == 'web').value;
            company.phone = this.informations.third.find((index) => index.key == 'phone').value;
            company.mobile = this.informations.third.find((index) => index.key == 'mobile').value;
            company.email = this.informations.third.find((index) => index.key == 'email').value;
            company.mainContact = this.informations.third.find(
                (index) => index.key == 'mainContact',
            ).value;
            company.isArchived = this.informations.third.find(
                (index) => index.key == 'isArchived',
            ).value;
            company.city = this.informations.third.find((index) => index.key == 'city').value;
            return company;
        },
        companyPhone() {
            return this.company.phone !== ''
                ? this.company.phone
                : this.company.mobile !== ''
                    ? this.company.mobile
                    : '';
        },
        isCorporation() {
            return this.company.type == 'corporation';
        },
        isThirdActive() {
            return this.company.active == true;
        },
        canEditDate() {
            return (
                !(this.opportunity.status === 'won' || this.opportunity.status === 'lost') &&
                this.permissions.can_edit
            );
        },
        opportunity() {
            const opportunity = {
                id: this.informations.identity.id,
                idName: this.informations.informations.find((index) => index.key === 'ident').value,
                potential: this.informations.informations.find(
                    (index) => index.key === 'potentialAmount',
                ).value,
                dueDate: moment(
                    this.informations.informations.find((index) => index.key === 'dueDate').value,
                ).format('LL'),
                endDate: moment(
                    this.informations.informations.find((index) => index.key === 'endDate').value,
                ).format('LL'),
                funnelName: this.informations.informations.find(
                    (index) => index.key === 'funnelName',
                ).value,
                steps: this.informations.informations.find((index) => index.key === 'steps').value,
                activeStep: this.informations.informations.find((index) => index.key === 'stepId')
                    .value,
                status: this.informations.informations.find((index) => index.key === 'signed')
                    .value,
                stepDate: this.informations.informations.find(
                    (index) => index.key === 'stepEnterDate',
                ).value,
                source: this.informations.informations.find((index) => index.key === 'sourceName')
                    .value,
                name: this.informations.informations.find((index) => index.key === 'name').value,
            };

            const collabs = this.informations.informations.find(
                (index) => index.key === 'staffs',
            ).value;

            opportunity.collab = {
                placeholder: this.$l10n.__('Collaborateurs'),
                list: collabs.map((collab) => {
                    return { ...collab, staffid: collab.id };
                }),
            };

            return opportunity;
        },

        statusDateClass() {
            const duedate = this.informations.informations.find(
                (index) => index.key == 'dueDate',
            ).value;
            return {
                'b-1-gray-60-hover p-2': !this.activeDatePicker,
                'b-1-warning-50 warning-05-bkg':
                    (moment().isAfter(duedate) && this.opportunity.status == 'open') ||
                    this.opportunity.status == 'late',
                'b-1-alert-50 alert-05-bkg': this.opportunity.status == 'lost',
                'b-1-success-50 success-05-bkg': this.opportunity.status == 'won',
                ' cpointer': this.canEditDate,
            };
        },

        statusDateIcon() {
            return {
                'icon-close': this.opportunity.status == 'lost',
                'icon-validate': this.opportunity.status == 'won',
                'icon-arrow-right':
                    this.opportunity.status == 'open' || this.opportunity.status == 'late',
            };
        },

        displayedStep() {
            return this.opportunity.steps.findIndex((step) => step.id === this.selectedStep);
        },

        statusesWithSelect() {
            return this.informations._meta.signedStatuses.map((status) => {
                const obj = Object.assign({}, status);
                status.id === this.opportunity.status
                    ? (obj.selected = true)
                    : (obj.selected = false);
                obj.color = allStatus[status.id].color;
                obj.icon = allStatus[status.id].icon;
                return obj;
            });
        },

        stepWithDate() {
            return this.informations.informations
                .find((index) => index.key == 'steps')
                .value.map((step) => {
                    const obj = Object.assign({}, step);
                    if (step.id == this.opportunity.activeStep) {
                        const nb = moment().diff(moment(this.opportunity.stepDate), 'days');
                        obj.desc =
                            nb == 0
                                ? this.$l10n.__("Aujourd'hui")
                                : this.$l10n.n__('%d jour', '%d jours', nb, nb);
                    }
                    return obj;
                });
        },

        statusRoute() {
            return (
                '/rest/prospection/opportunities/' +
                this.informations.identity.id +
                '/signed?_output=overview'
            );
        },
        selectedStep: {
            get() {
                return this.informations?.informations?.find((index) => index.key === 'stepId')
                    ?.value;
            },
            set(step) {
                this.$store.dispatch('updateOpportunityStepId', step);
            },
        },
        currentStatus() {
            return this.statusesWithSelect.find((status) => status.selected);
        },
        sharedStaff() {
            return this.$store.getters.sellsyInformations.sharedWithStaffs;
        },
        userPhoto() {
            if (this.data.avatar.type === 'picture') {
                return this.data.avatar.value;
            }
            return null;
        },
        allowedScheduledActions() {
            return this.scheduledActions.filter((item) => item.permission);
        },
        linkedid() {
            return this.$store.getters.linkedid;
        },
        linkedtype() {
            return this.$store.getters.linkedtype;
        },
        pipeName() {
            return `${this.$t.__('common.labels.pipeline')} : ${this.opportunity.funnelName}`;
        },
        formattedSources() {
            const sources = Object.values(
                this.informations.informations.find((index) => index.key === 'sources').value,
            );

            const selectedSourceId = this.informations.informations.find(
                (index) => index.key === 'sourceId',
            ).value;

            return sources.map((source) => {
                return {
                    id: source.id,
                    label: source.label,
                    selected: +source.id === selectedSourceId,
                };
            });
        },
        assignedStaffs() {
            return this.informations?.informations?.find((index) => index.key === 'staffs')?.value;
        },
    },
    created() {
        this.selectedStep = this.opportunity.activeStep;
        EventBus.on('opportunity:update', this.updateOpportunity);
        EventBus.on('opportunity:update:endDate', this.updateEndDate);
    },
    unmounted() {
        EventBus.off('opportunity:update', this.updateOpportunity);
        EventBus.off('opportunity:update:endDate', this.updateEndDate);
    },

    methods: {
        ...popupPanelMapActions(['open']),
        formatUrl,
        buildUrl,
        hasAccess,
        isBeforeSelectedStep(index) {
            return this.stepWithDate.findIndex((step) => step.id === this.selectedStep) > index;
        },
        handleStepSelectVisibleChange(visible) {
            this.blockHoverStep = visible;
        },
        stepHovered(step) {
            if (this.blockHoverStep) return;
            this.hoverStep = step;
        },
        pickStep(step, showToast = false) {
            if (!this.permissions.can_edit) {
                return;
            }
            this.selectedStep = step.id;
            this.setStep(showToast);
        },
        setStep(showToast) {
            const step = this.stepWithDate.find((step) => step.id === this.selectedStep);
            const self = this;
            if (step.hasAutomation) {
                this.$confirm(
                    this.$l10n.__(
                        "Une action d’envoi automatique d'email est activée sur cette étape. Confirmez-vous l'envoi de cet l’email ?",
                    ),
                    '',
                    {
                        confirmButtonText: this.$l10n.__("Envoyer l'email"),
                        cancelButtonText: this.$l10n.__("Ne pas envoyer l'email"),
                        confirmButtonClass: 'btn btn-primary',
                        cancelButtonClass: 'btn btn-default',
                    },
                )
                    // Send Mail
                    .then(() => {
                        this.$ajax.put(
                            `/rest/prospection/opportunities/${this.opportunity.id}/step`,
                            { _output: 'overview' },
                            { stepId: step.id, skipAutomation: false },
                            (response) => {
                                if (!showToast) {
                                    Push.success(this.$l10n.__('Email(s) envoyé(s)'));
                                } else {
                                    this.$pushToast({
                                        type: 'success',
                                        text: this.$l10n.__('Email(s) envoyé(s)'),
                                        close: true,
                                    });
                                }
                                self.$store.dispatch('updateStep', response.data.informations);
                            },
                            (error) => {
                                this.errors.setErrors(error.response.data.error.message);
                                if (!showToast) {
                                    Push.error(
                                        this.$l10n.__(
                                            'Une erreur est survenue lors de la mise à jour.',
                                        ),
                                    );
                                } else {
                                    this.$pushToast({
                                        type: 'error',
                                        text: this.$l10n.__(
                                            'Une erreur est survenue lors de la mise à jour.',
                                        ),
                                        close: true,
                                    });
                                }
                            },
                        );
                    })
                    // Skip mail
                    .catch(() => {
                        this.$ajax.put(
                            `/rest/prospection/opportunities/${this.opportunity.id}/step`,
                            { _output: 'overview' },
                            { stepId: step.id, skipAutomation: true },
                            (response) => {
                                if (!showToast) {
                                    Push.success(
                                        this.$l10n.__(
                                            "L'étape de l'opportunité a été mise à jour.",
                                        ),
                                    );
                                } else {
                                    this.$pushToast({
                                        type: 'success',
                                        text: this.$l10n.__(
                                            "L'étape de l'opportunité a été mise à jour.",
                                        ),
                                        close: true,
                                    });
                                }
                                self.$store.dispatch('updateStep', response.data.informations);
                            },
                            (error) => {
                                this.errors.setErrors(error.response.data.error.message);
                                if (!showToast) {
                                    Push.error(
                                        this.$l10n.__(
                                            'Une erreur est survenue lors de la mise à jour.',
                                        ),
                                    );
                                } else {
                                    this.$pushToast({
                                        type: 'error',
                                        text: this.$l10n.__(
                                            'Une erreur est survenue lors de la mise à jour.',
                                        ),
                                        close: true,
                                    });
                                }
                            },
                        );
                    });
            }
            // No automation
            else {
                this.$ajax.put(
                    `/rest/prospection/opportunities/${this.opportunity.id}/step`,
                    { _output: 'overview' },
                    { stepId: step.id, skipAutomation: true },
                    (response) => {
                        if (!showToast) {
                            Push.success(
                                this.$l10n.__("L'étape de l'opportunité a été mise à jour."),
                            );
                        } else {
                            this.$pushToast({
                                type: 'success',
                                text: this.$l10n.__("L'étape de l'opportunité a été mise à jour."),
                                close: true,
                            });
                        }
                        self.$store.dispatch('updateStep', response.data.informations);
                    },
                    (error) => {
                        this.errors.setErrors(error.response.data.error.message);
                        if (!showToast) {
                            Push.error(
                                this.$l10n.__('Une erreur est survenue lors de la mise à jour.'),
                            );
                        } else {
                            this.$pushToast({
                                type: 'error',
                                text: this.$l10n.__(
                                    'Une erreur est survenue lors de la mise à jour.',
                                ),
                                close: true,
                            });
                        }
                    },
                );
            }
        },

        addTimetracking() {
            window.TimetrackingEntry.edit('', this.opportunity.id, 'opportunity', () =>
                Push.success(this.$l10n.__('Sauvegarde effectuée')),
            );
        },

        editOpportunity() {
            this.open({
                key: 'opportunityForm',
                props: {
                    oppId: this.linkedid,
                    context: 'overview',
                    mode: 'edit',
                    action: 'edit',
                    emitEditSuccess: 'opportunity:update',
                },
            });
        },

        duplicateOpportunity() {
            this.open({
                key: 'opportunityForm',
                props: {
                    context: 'overview',
                    mode: 'edit',
                    action: 'duplicate',
                    oppId: this.$store.getters.linkedid,
                },
            });
        },

        // method required for dynamic call of local function from action selector
        handleFunctionCall(function_name) {
            this[function_name]();
        },

        updateOpportunity(payload) {
            this.$store.dispatch('updateInformations', payload.data.informations);
            this.$store.dispatch('updateSellsyInformations', payload.data.sellsy);
            EventBus.emit('overview:sellsyinformation:update', payload.data.sellsy);
            this.$store.dispatch('updateContacts', payload.data.contacts);
            this.$store.dispatch('updateThird', payload.data.third);
            this.selectedStep = payload.data.informations.find(
                (info) => info.key === 'stepId',
            ).value;
        },

        updateDueDate(date) {
            ApiClient.put(
                `/opportunities/${this.linkedid}`,
                {},
                {
                    due_date: date,
                    ident: this.opportunity.idName,
                    name: this.opportunity.name,
                    stepId: this.opportunity.activeStep,
                    funnelId: this.informations.informations.find(
                        (index) => index.key === 'funnelId',
                    ).value,
                    linkedid: this.informations.informations.find(
                        (index) => index.key === 'linkedid',
                    ).value,
                    linkedtype: this.informations.informations.find(
                        (index) => index.key === 'linkedtype',
                    ).value,
                },
            )
                .then((response) => {
                    // Update litteral date and computed base data with value
                    this.opportunity.endDate = moment(date).format('LL');
                    this.informations.informations.find((index) => index.key === 'endDate').value =
                        date;
                    Push.success(
                        this.$l10n.__("La date d'échéance a été mise à jour"),
                        this.$l10n.__('Opportunité modifiée'),
                    );
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);
                    Push.error(
                        this.$l10n.__(
                            "Une erreur est survenue lors de la modification de l'opportunité",
                        ),
                    );
                });
        },
        updateEndDate(payload) {
            if (payload.event && payload.event.end !== '') {
                this.$store.dispatch('updateEndDate', payload.event.end);
                Push.success(
                    this.$l10n.__("La date d'échéance a été mise à jour"),
                    this.$l10n.__('Opportunité modifiée'),
                );
            }
        },
        handleEndDateUpdate(value) {
            this.isLoadingDate = true;
            OpportunitiesService.updateOpportunity(this.linkedid, {
                due_date: value,
            })
                .then(() => {
                    // Update litteral date and computed base data with value
                    this.opportunity.endDate = moment(value).format('LL');
                    this.informations.informations.find((index) => index.key === 'endDate').value =
                        value;

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('opportunity.update.endDate.success'),
                    });
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);

                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('opportunity.update.endDate.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingDate = false;
                });
        },
        handleVisibleChange(visible) {
            this.$nextTick(() => {
                this.activeDatePicker = visible;
            });
        },
        openOppDatePicker() {
            if (this.canEditDate) {
                this.$refs.datepicker.handleOpen();
                this.activeDatePicker = true;
            }
        },
        handleActionItemClick(action) {
            switch (action.type) {
                case 'function':
                    this.handleFunctionCall(action.value);
                    break;
                case 'vuejs':
                    this.$router.push(this.buildUrl(action.value, this.parameters));
                    break;
                default:
                    window.location.href = this.buildUrl(action.value, this.parameters);
                    break;
            }
        },
        handleStatusUpdate(value) {
            this.isLoadingStatus = true;

            OpportunitiesService.updateOpportunity(this.linkedid, {
                status: value,
            })
                .then(() => {
                    this.$store.dispatch('updateActiveStatus', {
                        status: this.statusesWithSelect.find((item) => item.id === value),
                        endDate: this.endDate,
                    });

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('opportunity.update.status.success'),
                    });
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);

                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('opportunity.update.status.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingStatus = false;
                });
        },
        checkIsEmailSetUp() {
            this.checkEmailProvider({}, { checkPrefill: true });
        },
        handleClick(value) {
            const linkedType = this.linkedtype;
            const linkedid = this.linkedid;

            switch (value) {
                case 'task':
                    Activities.openActivityForm(
                        null,
                        linkedid,
                        linkedType,
                        function (data) {
                            window.EventBus.emit('timeline:processAndAdd', {
                                entity: 'task',
                                event: 'task.created',
                                objectid: data.id,
                            });
                        },
                        'task',
                    );
                    break;
                case 'event':
                    Activities.openActivityForm(null, linkedid, linkedType, null, 'event');
                    break;
                case 'email':
                    this.checkIsEmailSetUp();
                    break;
                case 'phonecallScheduled':
                    Activities.openPhoneCallForm(
                        null,
                        linkedid,
                        linkedType,
                        function (data) {
                            window.EventBus.emit('timeline:processAndAdd', {
                                entity: 'phonecall',
                                event: 'phonecall.created',
                                objectid: data.id,
                            });
                        },
                        true,
                    );
                    break;
            }
        },
        createDocument() {
            const relationType = this.informations.third.find(
                (index) => index.key === 'relationType',
            ).value;
            Third.newDoc(
                this.company.id,
                relationType,
                'opportunity',
                this.linkedid,
                'prospection',
            );
        },
        handleTitleUpdate(title) {
            this.isLoadingTitle = true;
            OpportunitiesService.updateOpportunity(this.linkedid, {
                name: title,
            })
                .then(() => {
                    this.$store.dispatch('updateName', title);

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('opportunity.update.title.success'),
                    });
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);

                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('opportunity.update.title.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingTitle = false;
                });
        },
        handlePotentialAmountUpdate(amount) {
            this.isLoadingAmount = true;

            const formattedAmount = amount.replaceAll(',', '.').replaceAll(' ', '');

            OpportunitiesService.updateOpportunity(this.linkedid, {
                amount: formattedAmount,
            })
                .then((response) => {
                    this.$store.dispatch('updatePotentialAmount', response.data.amount.value);

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('opportunity.update.potentialAmount.success'),
                    });
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);

                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('opportunity.update.potentialAmount.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingAmount = false;
                });
        },
        handleStaffUpdate(staff) {
            this.isLoadingStaff = true;

            OpportunitiesService.updateOpportunity(this.linkedid, {
                assigned_staff_ids: staff,
            })
                .then((response) => {
                    if (
                        !response.data.assigned_staff_ids ||
                        response.data.assigned_staff_ids.length === 0
                    ) {
                        this.$store.dispatch('updateAssignedStaff', []);
                    } else {
                        const assignedStaff = this.staff.filter((staff) => {
                            return response.data.assigned_staff_ids.includes(staff.id);
                        });

                        this.$store.dispatch('updateAssignedStaff', assignedStaff);
                    }

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('opportunity.update.staff.success'),
                    });
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);

                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('opportunity.update.staff.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingStaff = false;
                });
        },
        handleSourceUpdate(value) {
            this.isLoadingSource = true;

            OpportunitiesService.updateOpportunity(this.linkedid, {
                source: +value,
            })
                .then(() => {
                    this.$store.dispatch('updateSource', {
                        id: +value,
                        name: this.formattedSources.find((item) => item.id === value).label,
                    });

                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('opportunity.update.source.success'),
                    });
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.message);

                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('opportunity.update.source.error'),
                    });
                })
                .finally(() => {
                    this.isLoadingSource = false;
                });
        },
    },
};
</script>
<style lang="scss" scoped>
header {
    width: calc(100% + 56px);
}
.step-container {
    max-width: 660px;
    margin: 0 auto;
}

.overview-header {
    background: var(--colors-common-background-default);
    border-bottom: 1px solid var(--colors-common-border-lighter);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-0);
    justify-content: space-between;
    padding: var(--spacing-3) var(--spacing-4);
    transform: translate(-24px, -32px);
    width: calc(100% + 48px);

    &__backlink {
        display: flex;
        gap: var(--spacing-4);
        justify-content: space-between;

        &::v-deep {
            .compass-link__label {
                font-size: var(--font-size-12);
                line-height: var(--line-heights-16);
            }
        }
    }

    &__title-and-info {
        display: flex;
        gap: var(--spacing-4);
        justify-content: space-between;
    }

    &__info {
        flex: 1;
        min-width: 0;
    }

    &__title {
        &-content {
            margin: 0;
        }
    }

    &__company-and-source {
        align-items: center;
        display: flex;
        gap: var(--spacing-3);
        min-height: 28px;
    }

    &__company {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);
        flex-shrink: 0;
        max-width: 200px;
        overflow: hidden;

        &::v-deep {
            .compass-link {
                overflow: hidden;

                & > * {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
        }
    }

    &__pipeline {
        color: var(--colors-common-text-subtle-default);
        overflow: hidden;
        white-space: nowrap;

        &::v-deep {
            .compass-tooltip,
            .compass-tooltip__element {
                display: block;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }

    &__source,
    &__opportunity-id {
        color: var(--colors-common-text-subtle-default);
        flex-shrink: 0;
    }

    &__pipeline-and-actions {
        display: flex;
        gap: var(--spacing-4);
        justify-content: space-between;
    }

    &__step-container {
        max-width: 765px;
    }

    &__data {
        align-items: flex-start;
        color: var(--colors-common-text-standard-default);
        display: flex;
        gap: var(--spacing-1);
    }

    &__status {
        min-width: 140px;
    }

    &__end,
    &__potential {
        min-width: 140px;
        z-index: 1;
    }

    &__potential-amount {
        align-items: center;
        display: flex;
        min-height: 32px;
    }

    &__actions {
        align-items: center;
        display: flex;
        flex-shrink: 0;
        flex-direction: row-reverse;
        gap: var(--spacing-1);
    }
}

.status-select {
    width: 100%;

    &::v-deep {
        .compass-select {
            width: 100%;
        }
    }
}
</style>
<style lang="scss">
.opportunity-step-select {
    .compass-dropdown-option--selected {
        .opportunity-step-select__step,
        .opportunity-step-select__step-probability {
            font-weight: 600;
        }
    }

    .opportunity-step-select__step {
        &--before,
        &-probability {
            color: var(--colors-common-text-subtle-default);
        }

        &-probability {
            flex-shrink: 0;
        }

        &--current {
            color: var(--colors-common-text-primary-default);
        }
    }
}

.step-container .opp-step .el-step {
    padding-top: 0;

    .el-step__line {
        top: 4px;
    }

    .el-step__icon {
        width: 10px;
        height: 10px;
        border-width: 2px;
        border-style: solid;
        border-radius: 50%;
        border-color: inherit;
        display: inline-block;
        transition: all 0.3s ease-in-out;
    }

    .el-step__icon-inner {
        width: 6px;
        height: 6px;
    }

    .el-step__head {
        &.is-process {
            .el-step__icon-inner {
                background-color: var(--colors-component-background-actionable-active-default);
            }
        }
    }
    .el-step__description.is-process {
        color: grey;
    }

    &:hover {
        .el-step__head {
            &.is-wait {
                .el-step__icon {
                    border-color: var(--colors-sellsy-brand-primary-50-alpha100);
                }

                .el-step__icon-inner {
                    background-color: transparent;
                }
            }
        }
    }
}
.step-select {
    .el-input {
        .el-input__inner {
            border: none;
            text-align: center;
            padding-left: 30px;
        }
        .el-input__suffix {
            .el-input__icon {
                color: var(--colors-common-text-standard-default);
            }
        }
    }
}

.opp-date .el-date-editor.opp-date--picker {
    width: 166px;
}

.page-prospection-opportunities-overview .el-date-picker .available {
    &,
    & > div,
    & span {
        width: 40px;
        height: 40px;
    }
    &.current:hover {
        background-color: unset;
        border-radius: initial;
    }
    &:not(.current):hover {
        background: initial;

        & span:after {
            content: '';
            width: 38px;
            height: 38px;
            border-radius: 50%;
            top: 0;
            left: 0;
            position: absolute;
            border: solid 1px var(--colors-common-border-default-hover);
        }
    }

    &:not(.today):hover {
        color: initial !important;
    }
}
</style>
